// Typed API client. All monetary values are integer paise.

export interface EventSetting {
  id: number;
  eventName: string;
  wordmark: string;
  description: string;
  scheduledAt: string | null;
  venue: string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueFloor: string | null;
  venueMapUrl: string | null;
  venueStatus: "confirmed" | "under_consideration" | "awaiting";
  venueBookingNote: string;
  timingNote: string;
  lunchTime: string | null;
  status: string;
  totalCohort: number;
  seniorsCount: number;
  juniorsCount: number;
  expectedAttendees: number;
  participationRateBps: number;
  contributionPaise: number;
  perPersonExpensePaise: number;
  seniorContributionPaise: number;
  juniorContributionPaise: number;
  contingencyRateBps: number;
  transportSeats: number;
  contributorCount: number;
  actualAttendees: number;
  actualSeniorAttendees: number;
  actualJuniorAttendees: number;
  surplusPolicy: string;
  surplusCustomCount: number;
  videoUrl: string;
  updatedAt: string;
}

export interface PollOptionDto {
  id: number;
  label: string;
  description: string;
  meta: Record<string, unknown>;
  sortOrder: number;
  published: boolean;
  count?: number;
  percentage?: number;
}

export interface PollDto {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  status: string;
  opensAt: string | null;
  closesAt: string | null;
  allowChanges: boolean;
  visibility: string;
  maxChoices: number;
  rulesText: string;
  showCounts: boolean;
  sortOrder: number;
  options: PollOptionDto[];
  ballotsCast: number;
  resultsVisible: boolean;
  effectiveStatus: "scheduled" | "open" | "closed" | "cancelled" | "draft";
}

export interface BudgetSummary {
  totalEstimatedPaise: number;
  totalActualPaise: number;
  totalFixedEstimatedPaise: number;
  totalVariableEstimatedPaise: number;
  contingencyAllowancePaise: number;
  projectedTotalPaise: number;
  quotedTaxPaise: number;
  foodEstimatedPaise: number;
  foodPerAttendeePaise: number;
  transportEstimatedPaise: number;
  transportPerPassengerPaise: number;
  costPerAttendeePaise: number;
  expectedContributionIncomePaise: number;
  expectedSponsorshipPaise: number;
  expectedOtherIncomePaise: number;
  expectedIncomePaise: number;
  fundingGapPaise: number;
  recommendedContributionPaise: number;
  perCategory: Array<{ categoryId: number; name: string; estimatedPaise: number; actualPaise: number }>;
  taxUncertainCount: number;
  expectedAttendees: number;
}

export interface QuoteBreakdown {
  basePricePaise: number;
  gstPaise: number;
  gstBps: number | null;
  gstSource: "quoted_amount" | "quoted_rate" | "none";
  taxInclusive: boolean;
  convenienceFeePaise: number;
  discountPaise: number;
  otherChargesPaise: number;
  computedTotalPaise: number;
  quotedFinalTotalPaise: number | null;
  finalTotalPaise: number;
}

export interface QuoteDto {
  id: number;
  title: string;
  vendorName: string;
  channel: string;
  category: string;
  taxInclusive: boolean;
  gstRateBps: number | null;
  quoteRef: string;
  notes: string;
  breakdown: QuoteBreakdown;
}

export interface BudgetItemDto {
  id: number;
  categoryId: number;
  categoryName: string;
  description: string;
  quantity: number;
  unit: string;
  unitCostPaise: number;
  isPerAttendee: boolean;
  estimatedPaise: number | null;
  actualPaise: number | null;
  vendorRef: string | null;
  taxTreatment: string;
  gstRateBps: number | null;
  paymentStatus: string;
  notes: string;
  pricingHidden?: boolean;
}

export interface SurplusResult {
  actualIncomePaise: number;
  actualExpensePaise: number;
  surplusPaise: number;
  isDeficit: boolean;
  eligibleCount: number;
  eligiblePolicy: string;
  perStudentPaise: number;
  residualPaise: number;
  distributablePaise: number;
  shareRatio?: {
    seniorPerStudentPaise: number;
    juniorPerStudentPaise: number;
    seniorShareBps: number;
    juniorShareBps: number;
    seniorDistributablePaise: number;
    juniorDistributablePaise: number;
  };
}

export interface MenuCategoryDto {
  id: number;
  name: string;
  sortOrder: number;
}

export interface MenuPackageDto {
  id: number;
  name: string;
  description: string;
  perPersonPaise: number;
  published: boolean;
  sortOrder: number;
  totals: { perPersonPaise: number; expectedAttendees: number; totalPaise: number };
}

export interface MenuItemDto {
  id: number;
  categoryId: number;
  packageId: number | null;
  name: string;
  description: string;
  isVeg: boolean;
  dietTag: "veg" | "nonveg" | "beverage" | "water";
  imageUrl: string | null;
  vendorRef: string | null;
  perPersonPaise: number;
  published: boolean;
}

export interface IncomeRecordDto {
  id: number;
  source: string;
  description: string;
  amountPaise: number;
  kind: string;
  confirmed: boolean;
  receivedAt: string | null;
}


export interface PerPlateBreakdown {
  packageName: string;
  vendorName: string;
  quoteRef: string;
  basePerPlatePaise: number | null;
  gstPaise: number | null;
  gstBps: number | null;
  gstSource: "quoted_amount" | "quoted_rate" | "none";
  taxInclusive: boolean;
  otherChargesPaise: number;
  paymentFeePaise: number;
  discountPaise: number;
  finalPerPlatePaise: number | null;
  awaitingQuote: boolean;
  statusNote: string;
}

export interface PersonCost {
  role: "junior" | "senior";
  contributionPaise: number | null;
  foodCostPaise: number | null;
  additionalChargePaise: number;
  netPositionPaise: number | null;
  awaiting: boolean;
}

export interface PairCost {
  junior: PersonCost;
  senior: PersonCost;
  combinedPaise: number | null;
  packageLabel: string;
  note: string;
}

export interface ScenarioRow {
  key: string;
  label: string;
  seniors: number;
  juniors: number;
  total: number;
  seniorFoodPaise: number | null;
  juniorFoodPaise: number | null;
  totalFoodPaise: number | null;
  contributionIncomePaise: number | null;
  additionalExpensesPaise: number;
  fundingGapPaise: number | null;
  perAttendeePaise: number | null;
  isPlanningScenario: boolean;
}

export interface PaymentScenarioCell {
  valuePaise: number | null;
  note: string;
}

export interface PaymentScenarioTable {
  rows: Array<{ component: string; online: PaymentScenarioCell; offline: PaymentScenarioCell }>;
  onlineTotalPaise: number | null;
  offlineTotalPaise: number | null;
  differencePaise: number | null;
  differencePercent: number | null;
  disclaimer: string;
}

export interface PricingPayload {
  contributions: {
    seniorPaise: number;
    juniorPaise: number;
    pairPaise: number;
    seniorAwaiting: boolean;
    juniorAwaiting: boolean;
  };
  perPersonExpensePaise: number;
  perPersonExpenseLabel: string;
  refund: {
    policy: string;
    seniorShareBps: number;
    juniorShareBps: number;
    note: string;
  };
  scenarios: Array<{
    key: string;
    label: string;
    seniors: number;
    juniors: number;
    total: number;
    isPlanningScenario: boolean;
    expensePaise: number;
    contributionIncomePaise: number;
    gapPaise: number;
    perAttendeeExpensePaise: number;
  }>;
  vendorPricingHidden: true;
}

export interface PortalPayload {
  event: EventSetting;
  decisionStatus: { openPolls: number; scheduledPolls: number; decidedPolls: number; totalPolls: number };
  polls: PollDto[];
  announcements: Array<{ id: number; title: string; body: string; pinned: boolean; createdAt: string }>;
  faqs: Array<{ id: number; question: string; answer: string; sortOrder: number }>;
  budgetSummary: BudgetSummary;
  budgetItems: BudgetItemDto[];
  budgetCategories: Array<{ id: number; name: string; sortOrder: number }>;
  quotes: QuoteDto[];
  menu: { categories: MenuCategoryDto[]; packages: MenuPackageDto[]; items: MenuItemDto[] };
  pricing: PricingPayload;
  surplus: SurplusResult;
  incomeRecords: IncomeRecordDto[];
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const obj = data as { error?: string; message?: string } | null;
    throw new ApiError(res.status, obj?.error ?? "error", obj?.message ?? "Something went wrong.");
  }
  return data as T;
}

export const api = {
  portal: () => request<PortalPayload>("/api/portal"),
  polls: () => request<{ polls: PollDto[] }>("/api/polls"),
  vote: (pollId: number, code: string, optionIds: number[]) =>
    request<{ success: boolean; changed: boolean; message: string }>(`/api/polls/${pollId}/vote`, {
      method: "POST",
      body: JSON.stringify({ code, optionIds }),
    }),
  submitCombined: (code: string, ballots: Array<{ pollId: number; optionIds: number[] }>) =>
    request<{
      success: boolean;
      submitted: Array<{ pollTitle: string; selectedOptionIds: number[]; changed: boolean }>;
      polls: PollDto[];
      message: string;
    }>("/api/votes/combined", {
      method: "POST",
      body: JSON.stringify({ code, ballots }),
    }),
  // ---- Student accounts (identity-based voting, no voting codes) ----
  studentSignup: (data: { fullName: string; facultyNumber: string; contactNumber: string; password: string }) =>
    request<{ success: boolean; student: StudentDto }>("/api/students/signup", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  studentSignin: (facultyNumber: string, password: string) =>
    request<{ success: boolean; student: StudentDto }>("/api/students/signin", {
      method: "POST",
      body: JSON.stringify({ facultyNumber, password }),
    }),
  studentProfile: (data: { enrollmentNumber: string; semester: string; hasTransport: boolean; canPickupFriends?: boolean | null; dietary: "veg" | "non-veg" | "both" }) =>
    request<{ success: boolean; student: StudentDto }>("/api/students/profile", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  studentSetPassword: (password: string) =>
    request<{ success: boolean }>("/api/students/password", { method: "POST", body: JSON.stringify({ password }) }),
  forgotPassword: (facultyNumber: string) =>
    request<{ success: boolean; message: string }>("/api/students/forgot-password", {
      method: "POST",
      body: JSON.stringify({ facultyNumber }),
    }),
  checkResetToken: (token: string) =>
    request<{ valid: boolean }>(`/api/students/reset-password/${encodeURIComponent(token)}`),
  resetPassword: (token: string, password: string) =>
    request<{ success: boolean; message: string }>("/api/students/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),
  studentSignout: () => request<{ success: boolean }>("/api/students/signout", { method: "POST" }),
  studentMe: () => request<{ student: StudentDto }>("/api/students/me"),
  submitStudentCombined: (ballots: Array<{ pollId: number; optionIds: number[] }>) =>
    request<{
      success: boolean;
      submitted: Array<{ pollTitle: string; selectedOptionIds: number[]; changed: boolean }>;
      polls: PollDto[];
      message: string;
    }>("/api/students/votes/combined", {
      method: "POST",
      body: JSON.stringify({ ballots }),
    }),
  studentBallots: () =>
    request<{ ballots: Array<{ pollId: number; pollTitle: string; category: string; changedAt: string; selected: Array<{ id: number; label: string }>; resultsVisible: boolean }> }>(
      "/api/students/ballots",
    ),
  adminLedger: () =>
    request<{ students: Array<StudentDto & { contactNumber: string; email: string; hasTransport: boolean | null; canPickupFriends: boolean | null; dietary: string | null; registeredAt: string; lastSeenAt: string | null; ballots: Array<{ pollTitle: string; category: string; submittedAt: string; selected: string[] }> }> }>(
      "/api/admin/results/ledger",
    ),
  login: (username: string, password: string) =>
    request<{ success: boolean; username: string }>("/api/admin/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<{ success: boolean }>("/api/admin/logout", { method: "POST" }),
  me: () => request<{ username: string }>("/api/admin/me"),
  adminGet: <T>(path: string) => request<T>(`/api/admin${path}`),
  adminSend: <T>(path: string, method: string, body?: unknown) =>
    request<T>(`/api/admin${path}`, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
};

export function csvUrl(path: string): string {
  return path;
}

export interface StudentDto {
  id: number;
  fullName: string;
  facultyNumber: string;
  enrollmentNumber: string | null;
  semester: string | null;
  hasTransport: boolean | null;
  canPickupFriends: boolean | null;
  dietary: string | null;
  /** True once enrollment, semester, diet and transport are all answered. */
  profileComplete: boolean;
  /** False for accounts created before passwords existed. */
  passwordSet: boolean;
}
